/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication.
 * Redirects unauthenticated users to Entra External ID login.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";

/**
 * Loading spinner component
 */
const LoadingSpinner = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
    }}>
        <div style={{
            border: '4px solid var(--border-light)',
            borderTop: '4px solid var(--brand-2)',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite',
        }} />
        <p style={{ marginTop: '20px', color: 'var(--text-2)' }}>
            Authenticating...
        </p>
        <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </div>
);

/**
 * ProtectedRoute component
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Components to render if authenticated
 * @param {React.ReactNode} props.fallback - Optional fallback component while checking auth
 */
const ProtectedRoute = ({ children, fallback = <LoadingSpinner /> }) => {
    const { inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const location = useLocation();

    // Show loading state while authentication is in progress
    if (inProgress !== "none") {
        return fallback;
    }

    // Show loading state while checking authentication
    if (!isAuthenticated) {
        sessionStorage.setItem('intendedPath', location.pathname + location.search);
        return <Navigate to="/" replace />;
    }

    // User is authenticated, render protected content
    return <>{children}</>;
};

export default ProtectedRoute;
